import React, { FunctionComponent, useEffect, useState } from 'react'
import { Card, Spin, Typography, Tag, Descriptions, Alert } from 'antd'
import { useParams } from 'react-router'
import { InstrumentsService } from '../api/services.gen'
import { InstrumentDto } from '../api/types.gen'
import { ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

type InstrumentParams = { code: string }

// antd Descriptions headers only carry a bottom margin (and a generous one), so
// stacked groups need an explicit top margin to keep the sub-titles attached to
// their own rows instead of floating between two blocks
const sectionProps = {
  size: 'small' as const,
  column: 2,
  style: { marginTop: 16 },
  styles: { header: { marginBottom: 8 } },
}

const InstrumentPage: FunctionComponent = () => {
  const params = useParams<InstrumentParams>()
  const [loading, setLoading] = useState(true)
  const [instrument, setInstrument] = useState<InstrumentDto | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchInstrument = async () => {
      if (!params.code) {
        setError('No instrument code provided')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const result = await InstrumentsService.getInstrument({ code: params.code })
        if (result.instrument) {
          setInstrument(result.instrument)
        } else {
          setError('Instrument not found')
        }
      } catch (e: any) {
        console.error('Failed to fetch instrument:', e)
        setError(e.message || 'Failed to load instrument')
      } finally {
        setLoading(false)
      }
    }

    fetchInstrument()
  }, [params.code])

  if (loading) {
    return (
      <div>
        <Typography.Title level={3}>Instrument Details</Typography.Title>
        <Spin size="large" />
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <Typography.Title level={3}>Instrument Details</Typography.Title>
        <Alert message="Error" description={error} type="error" showIcon />
      </div>
    )
  }

  if (!instrument) {
    return (
      <div>
        <Typography.Title level={3}>Instrument Details</Typography.Title>
        <Alert message="Not Found" description="Instrument not found" type="warning" showIcon />
      </div>
    )
  }

  const displayName = instrument.name || instrument.code

  // Format large numbers with K, M, B, T suffixes
  const formatNumber = (value: number) => {
    if (value === 0) return '0'
    const abs = Math.abs(value)
    const sign = value < 0 ? '-' : ''
    
    if (abs >= 1e12) return sign + (abs / 1e12).toFixed(1) + 'T'
    if (abs >= 1e9) return sign + (abs / 1e9).toFixed(1) + 'B'
    if (abs >= 1e6) return sign + (abs / 1e6).toFixed(1) + 'M'
    if (abs >= 1e3) return sign + (abs / 1e3).toFixed(1) + 'K'
    return sign + abs.toFixed(0)
  }

  // Combine all data by year
  const combinedData = () => {
    const dataMap = new Map<string, any>()

    // Add shares count data (use latest value per year)
    instrument.sharesCounts?.forEach(sc => {
      const year = new Date(sc.date).getFullYear().toString()
      if (!dataMap.has(year)) {
        dataMap.set(year, { year })
      }
      dataMap.get(year)!.sharesCount = sc.count
    })

    // Add valuation data (use latest value per year)
    instrument.valuations?.forEach(v => {
      const year = new Date(v.date).getFullYear().toString()
      if (!dataMap.has(year)) {
        dataMap.set(year, { year })
      }
      dataMap.get(year)!.valuation = v.value
      dataMap.get(year)!.valuationCurrency = v.currency
    })

    // Add income data (sum all income values per year)
    instrument.incomes?.forEach(i => {
      const year = new Date(i.date).getFullYear().toString()
      if (!dataMap.has(year)) {
        dataMap.set(year, { year, income: 0 })
      }
      dataMap.get(year)!.income = (dataMap.get(year)!.income || 0) + i.value
      dataMap.get(year)!.incomeCurrency = i.currency
    })

    return Array.from(dataMap.values()).sort((a, b) => 
      parseInt(a.year) - parseInt(b.year)
    )
  }

  const hasAnyData = (instrument.sharesCounts && instrument.sharesCounts.length > 0) ||
                     (instrument.valuations && instrument.valuations.length > 0) ||
                     (instrument.incomes && instrument.incomes.length > 0)

  // Get representative currencies from the data
  const chartData = combinedData()
  const valuationCurrency = chartData.find(d => d.valuationCurrency)?.valuationCurrency || ''
  const incomeCurrency = chartData.find(d => d.incomeCurrency)?.incomeCurrency || ''

  return (
    <div>
      <Typography.Title level={3} style={{ marginBottom: 0 }}>{displayName}</Typography.Title>
      <Typography.Text type="secondary">{instrument.code}</Typography.Text>

      {instrument.cik && (
        <>
          <Typography.Title level={4}>Basic Information</Typography.Title>
          <Descriptions column={1}>
            <Descriptions.Item label="CIK">{instrument.cik}</Descriptions.Item>
          </Descriptions>
        </>
      )}

      <Typography.Title level={4}>Markets</Typography.Title>
      {instrument.markets && instrument.markets.length > 0 ? (
        <div style={{ marginBottom: 16 }}>
          {instrument.markets.map((market, index) => (
            <Card key={index} size="small" style={{ marginBottom: 12 }}>
              <div>
                <Tag color={market.default ? 'blue' : 'default'}>
                  {market.symbol} on {market.market?.name || market.market?.id}
                </Tag>
                {market.default && <Typography.Text type="secondary"> (Default)</Typography.Text>}
              </div>
              <Descriptions {...sectionProps} title="General">
                {market.isin && <Descriptions.Item label="ISIN">{market.isin}</Descriptions.Item>}
                {market.lei && <Descriptions.Item label="LEI">{market.lei}</Descriptions.Item>}
                <Descriptions.Item label="Currency">{market.currency}</Descriptions.Item>
                <Descriptions.Item label="Options">{market.hasOptions ? 'Yes' : 'No'}{market.hasWeeklyOptions ? ' (weekly)' : ''}</Descriptions.Item>
              </Descriptions>
              {(market.saxoUic != null || market.market?.saxoExchangeId || market.market?.saxoOptionExchangeId || (market.saxoGroupOptionRootIds && market.saxoGroupOptionRootIds.length > 0)) && (
                <Descriptions {...sectionProps} title="Saxo">
                  {market.saxoUic != null && <Descriptions.Item label="UIC">{market.saxoUic}</Descriptions.Item>}
                  {market.market?.saxoExchangeId && <Descriptions.Item label="Exchange">{market.market.saxoExchangeId}</Descriptions.Item>}
                  {market.market?.saxoOptionExchangeId && <Descriptions.Item label="Option Exchange">{market.market.saxoOptionExchangeId}</Descriptions.Item>}
                  {market.saxoGroupOptionRootIds && market.saxoGroupOptionRootIds.length > 0 && (
                    <Descriptions.Item label="Option Root IDs">{market.saxoGroupOptionRootIds.join(', ')}</Descriptions.Item>
                  )}
                </Descriptions>
              )}
              {(market.alpacaId || market.market?.alpacaExchangeId) && (
                <Descriptions {...sectionProps} title="Alpaca">
                  {market.alpacaId && <Descriptions.Item label="ID">{market.alpacaId}</Descriptions.Item>}
                  {market.market?.alpacaExchangeId && <Descriptions.Item label="Exchange">{market.market.alpacaExchangeId}</Descriptions.Item>}
                </Descriptions>
              )}
              {market.ibkrConId != null && (
                <Descriptions {...sectionProps} title="IBKR">
                  <Descriptions.Item label="ConId">{market.ibkrConId}</Descriptions.Item>
                </Descriptions>
              )}
            </Card>
          ))}
        </div>
      ) : (
        <Typography.Text type="secondary" style={{ marginBottom: 16, display: 'block' }}>No markets available</Typography.Text>
      )}

      <Typography.Title level={4}>Historical Data</Typography.Title>
      {hasAnyData ? (
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" />
            <YAxis yAxisId="left" label={{ value: 'Shares Count', angle: -90, position: 'insideLeft' }} tickFormatter={formatNumber} />
            <YAxis yAxisId="right" orientation="right" label={{ value: `Valuation${valuationCurrency ? ' (' + valuationCurrency + ')' : ''}`, angle: 90, position: 'insideRight' }} tickFormatter={formatNumber} />
            <YAxis yAxisId="income" orientation="right" label={{ value: `Income${incomeCurrency ? ' (' + incomeCurrency + ')' : ''}`, angle: 90, position: 'insideRight' }} tickFormatter={formatNumber} />
            <Tooltip formatter={(value: any, name: string | undefined, props: any) => {
              const formattedValue = formatNumber(Number(value))
              if (name?.startsWith('Valuation') && props.payload?.valuationCurrency) {
                return `${formattedValue} ${props.payload.valuationCurrency}`
              }
              if (name?.startsWith('Income') && props.payload?.incomeCurrency) {
                return `${formattedValue} ${props.payload.incomeCurrency}`
              }
              return formattedValue
            }} />
            <Legend />
            {instrument.sharesCounts && instrument.sharesCounts.length > 0 && (
              <Line yAxisId="left" type="monotone" dataKey="sharesCount" stroke="#8884d8" name="Shares Count" />
            )}
            {instrument.valuations && instrument.valuations.length > 0 && (
              <Line yAxisId="right" type="monotone" dataKey="valuation" stroke="#82ca9d" name={`Valuation${valuationCurrency ? ' (' + valuationCurrency + ')' : ''}`} />
            )}
            {instrument.incomes && instrument.incomes.length > 0 && (
              <Bar yAxisId="income" dataKey="income" fill="#ffc658" name={`Income${incomeCurrency ? ' (' + incomeCurrency + ')' : ''}`} />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      ) : (
        <Typography.Text type="secondary">No historical data available</Typography.Text>
      )}
    </div>
  )
}

export { InstrumentPage }
